import React from 'react';
import { observer } from 'mobx-react';

import { Modal } from 'ui/base/modal';
import { InputKeyEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { BackButton } from 'ui/base/button/back_button';
import { ScrollableArea } from 'ui/base/page/scrollable_area';

import { SearchInput, SearchItemProps } from 'ui/standard/input/search_input/search_input';

import { TextInput } from 'ui/public/input';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { TransportProtocol, getShortTransportProtocol } from 'assets/enums/protocols/protocols';

import baseStyles from './range_input_modal.module.css';
import Messages from './range_input_messages';
import { RangeInputStore } from './range_input_store';

const ALWAYS_TRUE = () => true;
const IDENTITY = (item: TransportProtocol|undefined) => getProtocolText(item);

export function getProtocolText(protocol: TransportProtocol|undefined): string {
  return protocol !== undefined ? getShortTransportProtocol(protocol) : Messages.anyProtocol();
}

type RangeInputStyles = {
  modalContainer: string;
  column: string;
  headerContainer: string;
  contentContainer: string;
  instructions: string;
  inputContainer: string;
  errorContainer: string;
  protocolDisplay: string;
  protocolDisplayCaret: string;
  protocolListItem: string;
  centered: string;
  accept: string;
};

const themedStyles: ThemedStyles<RangeInputStyles> = {
  [Theme.DAYLIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    inputContainer: baseStyles.inputContainer,
    errorContainer: baseStyles.errorContainer,
    protocolDisplay: baseStyles.protocolDisplay,
    protocolDisplayCaret: baseStyles.protocolDisplayCaret,
    protocolListItem: baseStyles.protocolListItem,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
  [Theme.MIDNIGHT]: {
    modalContainer: baseStyles.modalContainer,
    column: baseStyles.column,
    headerContainer: baseStyles.headerContainer,
    contentContainer: baseStyles.contentContainer,
    instructions: baseStyles.instructions,
    inputContainer: baseStyles.inputContainer,
    errorContainer: baseStyles.errorContainer,
    protocolDisplay: baseStyles.protocolDisplay,
    protocolDisplayCaret: baseStyles.protocolDisplayCaret,
    protocolListItem: baseStyles.protocolListItem,
    centered: baseStyles.centered,
    accept: baseStyles.accept,
  },
};

type TransportProtocolDisplayProps = {
  store: RangeInputStore;
};
const TransportProtocolDisplay = observer(({ store }: TransportProtocolDisplayProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.protocolDisplay} onClick={store.search.modal.showModal}>
      {getProtocolText(store.protocol.value)}
      <div className={styles.protocolDisplayCaret}>&#x25BC;</div>
    </div>
  );
});

type TransportProtocolItemProps = {
  store: RangeInputStore;
  data: TransportProtocol|undefined;
};
const TransportProtocolItem = observer(({ data, store }: TransportProtocolItemProps) => {
  const styles = useStyles(themedStyles);
  const onClick = React.useCallback(() => {
    store.search.modal.hideModal();
    store.protocol.setValue(data);
  }, [store, data]);

  return (
    <div className={styles.protocolListItem} onClick={onClick}>
      {getProtocolText(data)}
    </div>
  );
});

type TransportProtocolInputProps = {
  store: RangeInputStore;
};
const TransportProtocolInput = observer(({ store }: TransportProtocolInputProps) => {
  const DisplayComponent = React.useCallback(() => {
    return <TransportProtocolDisplay store={store}/>;
  }, [store]);

  const ItemComponent = React.useCallback(({ data }: SearchItemProps<TransportProtocol|undefined>) => {
    return <TransportProtocolItem store={store} data={data}/>;
  }, [store]);

  return (
    <SearchInput
      DisplayComponent={DisplayComponent}
      ItemComponent={ItemComponent}
      data={[undefined, TransportProtocol.TCP, TransportProtocol.UDP]}
      filterFunc={ALWAYS_TRUE}
      keyFunc={IDENTITY}
      placeholder={getProtocolText(undefined)}
      store={store.search}
    />
  );
});

type RangeInputModalProps = {
  store: RangeInputStore;
  instructions: string;
  saveMessage: string;
  onCommit(): void;
  invalidMessage: string;
};

export const RangeInputModal = observer(({
  store,
  instructions,
  saveMessage,
  onCommit,
  invalidMessage,
}: RangeInputModalProps) => {
  const styles = useStyles(themedStyles);

  /*
  The implementation here is a hack to get around the HTML restriction
  that forms cannot be nested. This 'modal' may or may not be contained
  by some enclosing form (though I think, in all usages, there's an
  enclosing form).

  Pressing 'Enter' on an input contained in a form
  triggers the 'submit' of that form, which we do not want here; we
  only want to call onCommit (the action of this 'modal'). So we
  capture the Enter event.

  The submit button is also a div so that it doesn't trigger a submit.

  I think a better implementation would be to not place modal components
  inside the components that trigger opening them. A special separate
  handling of modals might be better.
  */
  const handleKeyPress = React.useCallback((event: InputKeyEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onCommit();
    }
  }, [onCommit]);
  return (
    <Modal store={store.modal}>
      <div className={styles.modalContainer}>
        <ScrollableArea>
          <div className={styles.column}>
            <div className={styles.headerContainer}>
              <BackButton onClick={store.modal.hideModal}/>
            </div>
            <div className={styles.contentContainer}>
              <div>
                <div className={styles.instructions}>
                  {instructions}
                </div>
                <div className={styles.inputContainer}>
                  <TransportProtocolInput store={store}/>:
                  <TextInput
                    placeholder={Messages.fromPrompt()}
                    store={store.portFirst}
                    onKeyPress={handleKeyPress}/>-
                  <TextInput
                    placeholder={Messages.untilPrompt()}
                    store={store.portFinal}
                    onKeyPress={handleKeyPress}/>
                </div>
                {!store.isValid.value &&
                  <div className={styles.errorContainer}>
                    <ErrorMessage message={invalidMessage}/>
                  </div>
                }
                <div className={styles.centered} onClick={onCommit}>
                  <div className={styles.accept}>
                    {saveMessage}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollableArea>
      </div>
    </Modal>
  );
});
